// src/features/datev-weiterberechnung/components/DatevWeiterberechnungPage.tsx

import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload,
  FileText,
  Loader,
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  AlertTriangle,
  Info,
  ChevronDown,
  ChevronRight,
  Play
} from 'lucide-react';
import { datevInvoiceParserService } from '../services/datevInvoiceParserService';
import { datevWeiterberechnungService } from '../services/datevWeiterberechnungService';
import { datevKostenService } from '../services/datevKostenService';
import {
  WeiterberechnungAnalysis,
  MandantMatch,
  ExecutionResult,
  ParsedDatevInvoice,
  DatevKostenBereitsErfasst,
  DatevKostenErfassung
} from '../types';

// ============================================
// KONSTANTEN
// ============================================

const GERMAN_MONTHS = [
  '', 'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

type Phase = 'upload' | 'parsing' | 'preview' | 'executing' | 'result' | 'saving' | 'kostenErfasst';

// Anzahl der Zeilen, die in den Ranglisten zunächst angezeigt werden
const RANKING_TOP_N = 10;

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) + ' €';

const formatZeitpunkt = (iso: string) =>
  new Date(iso).toLocaleString('de-DE', {
    timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });

// ============================================
// HAUPTKOMPONENTE
// ============================================

export const DatevWeiterberechnungPage: React.FC = () => {
  const navigate = useNavigate();

  const [phase, setPhase] = useState<Phase>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [parsed, setParsed] = useState<ParsedDatevInvoice | null>(null);
  const [analysis, setAnalysis] = useState<WeiterberechnungAnalysis | null>(null);
  const [execResult, setExecResult] = useState<ExecutionResult | null>(null);
  const [bereitsErfasst, setBereitsErfasst] = useState<DatevKostenBereitsErfasst | null>(null);
  const [kostenErfassung, setKostenErfassung] = useState<DatevKostenErfassung | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Grund, warum die Rechnung nicht erfasst und nicht weiterberechnet werden darf
  const sperrgrund: string | null = (() => {
    if (!parsed) return null;
    if (!parsed.rechnungsnummer || !parsed.beraternummer) {
      return 'Rechnungsnummer oder Beraternummer konnten nicht aus der DATEV-Rechnung gelesen werden. ' +
        'Ohne beide Angaben ist keine Erfassung möglich.';
    }
    if (bereitsErfasst) {
      return `Diese DATEV-Rechnung (Nr. ${parsed.rechnungsnummer}) wurde am ${formatZeitpunkt(bereitsErfasst.erfasstAm)} ` +
        `bereits erfasst${bereitsErfasst.modus === 'weiterberechnung' ? ' und weiterberechnet' : ''}. ` +
        'Ein erneuter Upload ist gesperrt.';
    }
    return null;
  })();

  // ============================================
  // FILE HANDLING
  // ============================================

  const handleFileSelect = (selectedFile: File) => {
    if (!selectedFile.type.includes('pdf')) {
      setError('Bitte eine PDF-Datei auswählen.');
      return;
    }
    setFile(selectedFile);
    setError(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) handleFileSelect(droppedFile);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  // ============================================
  // ACTIONS
  // ============================================

  const handleAnalyze = async () => {
    if (!file) return;
    setPhase('parsing');
    setError(null);

    try {
      const parsedInvoice = await datevInvoiceParserService.parseInvoicePdf(file);

      if (parsedInvoice.mandanten.length === 0) {
        throw new Error(
          'In der PDF wurden keine Mandantenblöcke erkannt. ' +
          'Ist es tatsächlich eine DATEV-Rechnung?'
        );
      }

      const result = await datevWeiterberechnungService.analyze(parsedInvoice);
      const erfasst = await datevKostenService.pruefeBereitsErfasst(
        parsedInvoice.beraternummer,
        parsedInvoice.rechnungsnummer
      );
      setParsed(parsedInvoice);
      setBereitsErfasst(erfasst);
      setAnalysis(result);
      setPhase('preview');
    } catch (err) {
      console.error('[DatevWeiterberechnung] Analyse-Fehler:', err);
      setError(err instanceof Error ? err.message : 'Unbekannter Fehler bei der Analyse');
      setPhase('upload');
    }
  };

  /**
   * Gemeinsame Vorprüfung vor jeder Erfassung: keine Sperre und eine
   * nachweislich vollständig gelesene Rechnung.
   */
  const pruefeVorErfassung = (): boolean => {
    if (!analysis || !parsed) return false;
    if (sperrgrund) {
      setError(sperrgrund);
      return false;
    }
    const befunde = datevWeiterberechnungService.pruefeVollstaendigkeit(analysis);
    if (befunde.length > 0) {
      setError(
        'Die DATEV-Rechnung wurde nicht vollständig gelesen. Es wurde nichts gespeichert.\n\n' +
        befunde.join('\n')
      );
      return false;
    }
    return true;
  };

  const handleExecute = async () => {
    if (!analysis || !parsed) return;

    const okCount = analysis.summary.matchesOk;
    if (okCount === 0) {
      setError('Keine Kunden zum Ausführen vorhanden.');
      return;
    }

    if (!pruefeVorErfassung()) return;

    const confirmMsg = `Weiterberechnung für ${okCount} Kunde(n) ausführen? ` +
      `Gesamtbetrag: ${formatCurrency(analysis.summary.totalBetragNetto)} netto.`;
    if (!window.confirm(confirmMsg)) return;

    setPhase('executing');
    setError(null);

    // Schritt 1: Rechnung als Kosten erfassen. Schlägt das fehl, wird nichts weiterberechnet.
    try {
      const erfassung = await datevKostenService.speichern(parsed, file ? file.name : null, 'weiterberechnung');
      setKostenErfassung(erfassung);
      setBereitsErfasst({ erfasstAm: new Date().toISOString(), modus: 'weiterberechnung' });
    } catch (err) {
      console.error('[DatevWeiterberechnung] Kostenerfassung fehlgeschlagen:', err);
      setError(err instanceof Error ? err.message : 'Unbekannter Fehler bei der Kostenerfassung');
      setPhase('preview');
      return;
    }

    // Schritt 2: Weiterberechnung
    try {
      const result = await datevWeiterberechnungService.execute(analysis);
      setExecResult(result);
      setPhase('result');
    } catch (err) {
      console.error('[DatevWeiterberechnung] Ausführungs-Fehler:', err);
      setError(
        'Die DATEV-Kosten wurden erfasst, die Weiterberechnung ist jedoch fehlgeschlagen: ' +
        (err instanceof Error ? err.message : 'Unbekannter Fehler bei der Ausführung')
      );
      setPhase('preview');
    }
  };

  const handleNurKosten = async () => {
    if (!analysis || !parsed) return;
    if (!pruefeVorErfassung()) return;

    const confirmMsg = `DATEV-Rechnung ${GERMAN_MONTHS[parsed.month]} ${parsed.year} nur als Kosten erfassen? ` +
      'Es werden keine Positionen in Kundenrechnungen geschrieben.';
    if (!window.confirm(confirmMsg)) return;

    setPhase('saving');
    setError(null);

    try {
      const erfassung = await datevKostenService.speichern(parsed, file ? file.name : null, 'nur_kosten');
      setKostenErfassung(erfassung);
      setBereitsErfasst({ erfasstAm: new Date().toISOString(), modus: 'nur_kosten' });
      setPhase('kostenErfasst');
    } catch (err) {
      console.error('[DatevWeiterberechnung] Kostenerfassung fehlgeschlagen:', err);
      setError(err instanceof Error ? err.message : 'Unbekannter Fehler bei der Kostenerfassung');
      setPhase('preview');
    }
  };

  const handleReset = () => {
    setFile(null);
    setParsed(null);
    setAnalysis(null);
    setExecResult(null);
    setBereitsErfasst(null);
    setKostenErfassung(null);
    setError(null);
    setPhase('upload');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ============================================
  // RENDER
  // ============================================

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px 0' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
        <button
          onClick={() => navigate('/rechnungen')}
          style={{
            padding: '8px 12px', backgroundColor: '#F3F4F6', border: '1px solid #E5E7EB',
            borderRadius: '6px', cursor: 'pointer', fontSize: '14px', color: '#374151',
            display: 'flex', alignItems: 'center', gap: '6px'
          }}
        >
          <ArrowLeft size={16} /> Zurück zu Rechnungen
        </button>
        <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#111827', margin: 0 }}>
          DATEV-Weiterberechnung
        </h1>
      </div>

      {/* Fehler-Banner */}
      {error && (
        <div style={{
          padding: '16px', backgroundColor: '#FEE2E2', borderRadius: '8px',
          marginBottom: '24px', display: 'flex', alignItems: 'start', gap: '12px',
          border: '1px solid #FECACA'
        }}>
          <AlertCircle size={20} color="#DC2626" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: '14px', color: '#991B1B', fontWeight: '500', margin: '0 0 4px' }}>
              Fehler
            </p>
            <p style={{ fontSize: '14px', color: '#991B1B', margin: 0 }}>{error}</p>
          </div>
        </div>
      )}

      {/* Phase-spezifischer Inhalt */}
      {(phase === 'upload' || phase === 'parsing') && renderUploadPhase()}
      {phase === 'preview' && analysis && renderPreviewPhase(analysis)}
      {phase === 'executing' && renderExecutingPhase()}
      {phase === 'saving' && renderSavingPhase()}
      {phase === 'result' && execResult && renderResultPhase(execResult)}
      {phase === 'kostenErfasst' && kostenErfassung && renderKostenErfasstPhase(kostenErfassung)}
    </div>
  );

  // ============================================
  // PHASE: UPLOAD
  // ============================================

  function renderUploadPhase() {
    const parsing = phase === 'parsing';

    return (
      <div>
        <div style={{
          padding: '16px 20px', backgroundColor: '#EFF6FF', borderRadius: '8px',
          border: '1px solid #BFDBFE', marginBottom: '20px', display: 'flex', gap: '12px'
        }}>
          <Info size={20} color="#2563EB" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div>
            <p style={{ fontSize: '14px', color: '#1E40AF', margin: '0 0 6px', fontWeight: '500' }}>
              So funktioniert's
            </p>
            <p style={{ fontSize: '13px', color: '#1E40AF', margin: 0, lineHeight: '1.5' }}>
              Monatliche DATEV-Rechnung hochladen. Das System erkennt automatisch den Abrechnungsmonat
              sowie pro Mandant die weiterzuberechnenden Posten (Arbeitnehmer Online, Postversand-Artikel).
              In der Vorschau kannst Du alles prüfen, bevor die Positionen in die Rechnungsentwürfe geschrieben werden.
              Dabei wird die Rechnung als Kostengrundlage für die Unit Economics gespeichert. Jede Rechnung kann
              nur einmal erfasst werden.
            </p>
          </div>
        </div>

        {/* Upload-Zone */}
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !parsing && fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${dragActive ? '#3B82F6' : '#D1D5DB'}`,
            borderRadius: '12px', padding: '48px 32px', textAlign: 'center',
            backgroundColor: dragActive ? '#F0F9FF' : '#F9FAFB',
            transition: 'all 0.2s', marginBottom: '24px',
            cursor: parsing ? 'not-allowed' : 'pointer',
            opacity: parsing ? 0.6 : 1
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFileSelect(f);
            }}
            style={{ display: 'none' }}
          />

          {file ? (
            <>
              <FileText size={64} color="#3B82F6" style={{ margin: '0 auto 24px' }} />
              <p style={{ fontSize: '18px', color: '#374151', margin: '0 0 8px', fontWeight: '500' }}>
                {file.name}
              </p>
              <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </>
          ) : (
            <>
              <Upload size={64} color="#9CA3AF" style={{ margin: '0 auto 24px' }} />
              <p style={{ fontSize: '18px', color: '#374151', margin: '0 0 8px', fontWeight: '500' }}>
                DATEV-Rechnung hochladen
              </p>
              <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '16px' }}>
                PDF-Datei hierhin ziehen oder klicken
              </p>
            </>
          )}
        </div>

        {/* Action-Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          {file && !parsing && (
            <button
              onClick={handleReset}
              style={{
                padding: '12px 24px', backgroundColor: '#F3F4F6', color: '#374151',
                border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer'
              }}
            >
              Zurücksetzen
            </button>
          )}
          <button
            onClick={handleAnalyze}
            disabled={!file || parsing}
            style={{
              padding: '12px 24px',
              backgroundColor: !file || parsing ? '#9CA3AF' : '#3B82F6',
              color: '#FFFFFF', border: 'none', borderRadius: '8px',
              fontSize: '14px', fontWeight: '500',
              cursor: !file || parsing ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: '8px'
            }}
          >
            {parsing ? (
              <>
                <Loader size={16} className="animate-spin" />
                PDF wird analysiert…
              </>
            ) : (
              <>Analysieren</>
            )}
          </button>
        </div>
      </div>
    );
  }

  // ============================================
  // PHASE: PREVIEW
  // ============================================

  function renderPreviewPhase(a: WeiterberechnungAnalysis) {
    const ausfuehrenGesperrt = a.summary.matchesOk === 0 || !!sperrgrund;
    const nurKostenGesperrt = !!sperrgrund;

    return (
      <div>
        {/* Sperrhinweis */}
        {sperrgrund && (
          <div style={{
            padding: '16px 20px', backgroundColor: '#FEF3C7', borderRadius: '8px',
            border: '1px solid #FDE68A', marginBottom: '20px', display: 'flex', gap: '12px'
          }}>
            <AlertTriangle size={20} color="#B45309" style={{ flexShrink: 0, marginTop: '2px' }} />
            <p style={{ fontSize: '14px', color: '#92400E', margin: 0, lineHeight: '1.5' }}>
              {sperrgrund}
            </p>
          </div>
        )}

        {/* Zusammenfassung */}
        <div style={{
          padding: '20px', backgroundColor: '#FFFFFF', borderRadius: '10px',
          border: '1px solid #E5E7EB', marginBottom: '20px'
        }}>
          <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: '0 0 16px' }}>
            Abrechnungsmonat {GERMAN_MONTHS[a.sourceMonth]} {a.sourceYear}
          </h2>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px'
          }}>
            <SummaryBox label="Zu berechnen" value={a.summary.matchesOk.toString()} color="#10B981" />
            <SummaryBox label="Gesamtbetrag netto" value={formatCurrency(a.summary.totalBetragNetto)} color="#10B981" />
            {a.summary.matchesNoWhitelist > 0 && (
              <SummaryBox label="Ohne Whitelist-Posten" value={a.summary.matchesNoWhitelist.toString()} color="#6B7280" />
            )}
            {a.summary.matchesSammelkunde > 0 && (
              <SummaryBox label="Sammelkunden (skip)" value={a.summary.matchesSammelkunde.toString()} color="#F59E0B" />
            )}
            {a.summary.matchesNotFound > 0 && (
              <SummaryBox label="Nicht gefunden" value={a.summary.matchesNotFound.toString()} color="#EF4444" />
            )}
            {a.summary.matchesDeleted > 0 && (
              <SummaryBox label="Gelöscht" value={a.summary.matchesDeleted.toString()} color="#EF4444" />
            )}
          </div>
        </div>

        {/* Tabelle */}
        <MatchesTable matches={a.matches} />

        {/* Analyse (nachgelagert) */}
        <AnalyseSection analysis={a} />

        {/* Action-Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
          <button
            onClick={handleReset}
            style={{
              padding: '12px 24px', backgroundColor: '#F3F4F6', color: '#374151',
              border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer'
            }}
          >
            Abbrechen
          </button>
          <button
            onClick={handleNurKosten}
            disabled={nurKostenGesperrt}
            title="Speichert die Rechnung für die Unit Economics, ohne Positionen in Kundenrechnungen zu schreiben"
            style={{
              padding: '12px 24px',
              backgroundColor: nurKostenGesperrt ? '#E5E7EB' : '#FFFFFF',
              color: nurKostenGesperrt ? '#9CA3AF' : '#374151',
              border: '1px solid #D1D5DB', borderRadius: '8px',
              fontSize: '14px', fontWeight: '500',
              cursor: nurKostenGesperrt ? 'not-allowed' : 'pointer'
            }}
          >
            Nur Kosten erfassen
          </button>
          <button
            onClick={handleExecute}
            disabled={ausfuehrenGesperrt}
            style={{
              padding: '12px 24px',
              backgroundColor: ausfuehrenGesperrt ? '#9CA3AF' : '#10B981',
              color: '#FFFFFF', border: 'none', borderRadius: '8px',
              fontSize: '14px', fontWeight: '500',
              cursor: ausfuehrenGesperrt ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: '8px'
            }}
          >
            <Play size={16} />
            Weiterberechnung ausführen ({a.summary.matchesOk})
          </button>
        </div>
      </div>
    );
  }

  // ============================================
  // PHASE: EXECUTING
  // ============================================

  function renderExecutingPhase() {
    return (
      <div style={{
        padding: '60px 20px', backgroundColor: '#FFFFFF', borderRadius: '10px',
        border: '1px solid #E5E7EB', textAlign: 'center'
      }}>
        <Loader size={48} color="#3B82F6" style={{ margin: '0 auto 16px' }} className="animate-spin" />
        <p style={{ fontSize: '16px', color: '#374151', margin: 0, fontWeight: '500' }}>
          Rechnungen werden angereichert…
        </p>
        <p style={{ fontSize: '14px', color: '#6B7280', margin: '8px 0 0' }}>
          Bitte nicht schließen. Das kann einen Moment dauern.
        </p>
      </div>
    );
  }

  // ============================================
  // PHASE: SAVING (nur Kosten)
  // ============================================

  function renderSavingPhase() {
    return (
      <div style={{
        padding: '60px 20px', backgroundColor: '#FFFFFF', borderRadius: '10px',
        border: '1px solid #E5E7EB', textAlign: 'center'
      }}>
        <Loader size={48} color="#3B82F6" style={{ margin: '0 auto 16px' }} className="animate-spin" />
        <p style={{ fontSize: '16px', color: '#374151', margin: 0, fontWeight: '500' }}>
          DATEV-Rechnung wird erfasst…
        </p>
      </div>
    );
  }

  // ============================================
  // PHASE: RESULT
  // ============================================

  function renderResultPhase(r: ExecutionResult) {
    const totalProcessed = r.createdInvoices.length + r.appendedInvoices.length;
    const successful = r.errors.length === 0;

    return (
      <div>
        <div style={{
          padding: '24px', backgroundColor: successful ? '#F0FDF4' : '#FEF3C7',
          borderRadius: '10px', border: `1px solid ${successful ? '#BBF7D0' : '#FDE68A'}`,
          marginBottom: '24px', display: 'flex', alignItems: 'start', gap: '16px'
        }}>
          {successful
            ? <CheckCircle2 size={32} color="#059669" style={{ flexShrink: 0 }} />
            : <AlertTriangle size={32} color="#D97706" style={{ flexShrink: 0 }} />
          }
          <div style={{ flex: 1 }}>
            <h2 style={{
              fontSize: '18px', fontWeight: '600',
              color: successful ? '#065F46' : '#92400E', margin: '0 0 8px'
            }}>
              {successful ? 'Weiterberechnung erfolgreich' : 'Teilweise erfolgreich'}
            </h2>
            <p style={{
              fontSize: '14px', color: successful ? '#047857' : '#B45309',
              margin: 0, lineHeight: '1.5'
            }}>
              {totalProcessed} Rechnung(en) verarbeitet: {r.createdInvoices.length} neu angelegt,
              {' '}{r.appendedInvoices.length} ergänzt.
              {r.errors.length > 0 && ` ${r.errors.length} Fehler.`}
              {kostenErfassung && ` Die DATEV-Rechnung ist als Kostengrundlage erfasst.`}
            </p>
          </div>
        </div>

        {/* Erfolgs-Liste */}
        {r.createdInvoices.length > 0 && (
          <ResultSection title={`Neu angelegte Entwürfe (${r.createdInvoices.length})`} color="#10B981">
            {r.createdInvoices.map(inv => (
              <div key={inv.invoiceId} style={resultRowStyle}>
                <span style={{ flex: 1 }}>{inv.customerCompany}</span>
                <span style={{ color: '#6B7280', fontSize: '13px' }}>
                  {GERMAN_MONTHS[inv.month]} {inv.year}
                </span>
              </div>
            ))}
          </ResultSection>
        )}

        {r.appendedInvoices.length > 0 && (
          <ResultSection title={`Ergänzte Entwürfe (${r.appendedInvoices.length})`} color="#3B82F6">
            {r.appendedInvoices.map(inv => (
              <div key={inv.invoiceId} style={resultRowStyle}>
                <span style={{ flex: 1 }}>{inv.customerCompany}</span>
                <span style={{ color: '#6B7280', fontSize: '13px' }}>
                  {GERMAN_MONTHS[inv.month]} {inv.year} (+{inv.positionsAdded} Pos.)
                </span>
              </div>
            ))}
          </ResultSection>
        )}

        {r.errors.length > 0 && (
          <ResultSection title={`Fehler (${r.errors.length})`} color="#EF4444">
            {r.errors.map((e, idx) => (
              <div key={idx} style={resultRowStyle}>
                <span style={{ flex: 1 }}>{e.customerCompany}</span>
                <span style={{ color: '#DC2626', fontSize: '13px' }}>{e.error}</span>
              </div>
            ))}
          </ResultSection>
        )}

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
          <button
            onClick={handleReset}
            style={{
              padding: '12px 24px', backgroundColor: '#F3F4F6', color: '#374151',
              border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer'
            }}
          >
            Weitere PDF hochladen
          </button>
          <button
            onClick={() => navigate('/rechnungen')}
            style={{
              padding: '12px 24px', backgroundColor: '#3B82F6', color: '#FFFFFF',
              border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer'
            }}
          >
            Zu den Rechnungen
          </button>
        </div>
      </div>
    );
  }

  // ============================================
  // PHASE: KOSTEN ERFASST (ohne Weiterberechnung)
  // ============================================

  function renderKostenErfasstPhase(k: DatevKostenErfassung) {
    const summeMandanten = k.bloecke
      .filter(b => b.kategorie === 'mandant')
      .reduce((sum, b) => sum + b.kostenOhneBonus + b.bonusAnteil, 0);
    const summeAllgemein = k.bloecke
      .filter(b => b.kategorie === 'allgemein')
      .reduce((sum, b) => sum + b.kostenOhneBonus + b.bonusAnteil, 0);
    const nichtZuordenbar = k.bloecke.filter(b => b.kategorie === 'nicht_zuordenbar');
    const summeNichtZuordenbar = nichtZuordenbar.reduce((sum, b) => sum + b.kostenOhneBonus + b.bonusAnteil, 0);

    return (
      <div>
        <div style={{
          padding: '24px', backgroundColor: '#F0FDF4',
          borderRadius: '10px', border: '1px solid #BBF7D0',
          marginBottom: '24px', display: 'flex', alignItems: 'start', gap: '16px'
        }}>
          <CheckCircle2 size={32} color="#059669" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#065F46', margin: '0 0 8px' }}>
              DATEV-Rechnung {GERMAN_MONTHS[k.month]} {k.year} erfasst
            </h2>
            <p style={{ fontSize: '14px', color: '#047857', margin: 0, lineHeight: '1.5' }}>
              Rechnung Nr. {k.rechnungsnummer} mit {formatCurrency(k.nettoSumme)} netto ist als Kostengrundlage
              für die Unit Economics gespeichert. Es wurden keine Positionen in Kundenrechnungen geschrieben.
            </p>
          </div>
        </div>

        <div style={{
          padding: '20px', backgroundColor: '#FFFFFF', borderRadius: '10px',
          border: '1px solid #E5E7EB', marginBottom: '20px'
        }}>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '12px'
          }}>
            <SummaryBox label="Mandantenbezogen inkl. Bonus" value={formatCurrency(summeMandanten)} color="#374151" />
            <SummaryBox label="DATEV allgemein" value={formatCurrency(summeAllgemein)} color="#374151" />
            <SummaryBox label="Abrechnungsbonus verteilt" value={formatCurrency(k.abrechnungsbonus)} color="#10B981" />
            <SummaryBox label="Abrechnungsgrundpreise" value={k.grundpreisMenge.toString()} color="#374151" />
            {nichtZuordenbar.length > 0 && (
              <SummaryBox label="Nicht zuordenbar" value={formatCurrency(summeNichtZuordenbar)} color="#F59E0B" />
            )}
          </div>
          {nichtZuordenbar.length > 0 && (
            <div style={{ marginTop: '12px', fontSize: '12px', color: '#92400E' }}>
              Ohne aktiven Kunden: {nichtZuordenbar.map(b => `${b.datevMandantennummer} ${b.datevName}`).join(', ')}.
              Diese Beträge laufen in den Unit Economics unter DATEV allgemein.
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
          <button
            onClick={handleReset}
            style={{
              padding: '12px 24px', backgroundColor: '#3B82F6', color: '#FFFFFF',
              border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer'
            }}
          >
            Weitere PDF hochladen
          </button>
        </div>
      </div>
    );
  }
};

// ============================================
// SUB-KOMPONENTEN
// ============================================

const SummaryBox: React.FC<{ label: string; value: string; color: string }> = ({ label, value, color }) => (
  <div style={{
    padding: '12px 16px', backgroundColor: '#F9FAFB', borderRadius: '8px',
    border: '1px solid #E5E7EB'
  }}>
    <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>{label}</div>
    <div style={{ fontSize: '18px', fontWeight: '600', color }}>{value}</div>
  </div>
);

const ResultSection: React.FC<{ title: string; color: string; children: React.ReactNode }> = ({ title, color, children }) => (
  <div style={{
    marginBottom: '16px', backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB',
    borderRadius: '8px', overflow: 'hidden'
  }}>
    <div style={{
      padding: '10px 16px', backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB',
      fontSize: '14px', fontWeight: '600', color
    }}>
      {title}
    </div>
    <div>{children}</div>
  </div>
);

const resultRowStyle: React.CSSProperties = {
  padding: '10px 16px', display: 'flex', gap: '12px', alignItems: 'center',
  borderBottom: '1px solid #F3F4F6', fontSize: '14px', color: '#374151'
};

const detailCellStyle: React.CSSProperties = { color: '#374151' };

const detailHeaderStyle: React.CSSProperties = { color: '#6B7280', fontWeight: 500 };

// ============================================
// MATCHES-TABELLE
// ============================================

const MatchesTable: React.FC<{ matches: MandantMatch[] }> = ({ matches }) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Sortierung: OK zuerst, dann no_whitelist, dann Warnungen, Beraterblock zuletzt
  const sorted = [...matches].sort((a, b) => {
    const order: Record<string, number> = {
      'ok': 0,
      'no_whitelist_items': 1,
      'sammelkunde_skipped': 2,
      'customer_not_found': 3,
      'customer_deleted': 4,
      'berater_block': 5
    };
    return (order[a.status] ?? 99) - (order[b.status] ?? 99);
  });

  const toggle = (key: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div style={{
      backgroundColor: '#FFFFFF', borderRadius: '10px',
      border: '1px solid #E5E7EB', overflow: 'hidden'
    }}>
      <div style={{
        padding: '12px 16px', backgroundColor: '#F9FAFB',
        borderBottom: '1px solid #E5E7EB', fontSize: '14px', fontWeight: '600', color: '#111827'
      }}>
        Mandanten-Matches ({matches.length})
      </div>
      <div>
        {sorted.map(m => (
          <MatchRow
            key={m.datevMandantennummer}
            match={m}
            expanded={expanded.has(m.datevMandantennummer)}
            onToggle={() => toggle(m.datevMandantennummer)}
          />
        ))}
      </div>
    </div>
  );
};

// ============================================
// EINZELNE TABELLEN-ZEILE
// ============================================

const MatchRow: React.FC<{ match: MandantMatch; expanded: boolean; onToggle: () => void }> = ({
  match, expanded, onToggle
}) => {
  const statusInfo = getStatusInfo(match.status);

  // Aufklappbar, sobald es irgendwelche Posten zu zeigen gibt – auch bei
  // Sammelkunden, nicht gefundenen Mandanten und beim beraterbezogenen Block.
  const canExpand =
    match.aggregatedPositions.length > 0 ||
    match.skippedBillablePositions.length > 0 ||
    match.nonBillablePositions.length > 0;

  // Summe dessen, was NICHT weiterberechnet wird: nicht whitelistfähige Posten
  // plus whitelistfähige Posten übersprungener Kunden.
  const nichtWeiterberechnet = Math.round((match.totalNonBillable + match.totalSkippedBillable) * 100) / 100;

  return (
    <div style={{ borderBottom: '1px solid #F3F4F6' }}>
      {/* Hauptzeile */}
      <div
        onClick={canExpand ? onToggle : undefined}
        style={{
          padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px',
          cursor: canExpand ? 'pointer' : 'default',
          backgroundColor: '#FFFFFF'
        }}
      >
        {/* Expand-Icon */}
        <div style={{ width: '20px', flexShrink: 0 }}>
          {canExpand && (expanded
            ? <ChevronDown size={16} color="#6B7280" />
            : <ChevronRight size={16} color="#6B7280" />
          )}
        </div>

        {/* Status-Badge */}
        <div style={{
          padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '500',
          backgroundColor: statusInfo.bg, color: statusInfo.color,
          border: `1px solid ${statusInfo.border}`,
          flexShrink: 0, whiteSpace: 'nowrap'
        }}>
          {statusInfo.label}
        </div>

        {/* Mandant + Kunde */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
            {match.customerCompany || match.datevName}
          </div>
          <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>
            {match.istBeraterBlock
              ? 'Ohne Mandantenbezug'
              : <>DATEV-Mandant {match.datevMandantennummer}{match.customerCompany && ` • ${match.datevName}`}</>
            }
          </div>
        </div>

        {/* Ziel-Rechnung */}
        {match.targetInvoice && (
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontSize: '13px', color: '#374151', fontWeight: '500' }}>
              {getActionLabel(match.targetInvoice.action)}
            </div>
            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>
              {GERMAN_MONTHS[match.targetInvoice.targetMonth]} {match.targetInvoice.targetYear}
            </div>
          </div>
        )}

        {/* Nicht weiterberechnet (Analyse, bewusst untergeordnet dargestellt) */}
        <div style={{ minWidth: '105px', textAlign: 'right', flexShrink: 0 }}>
          {nichtWeiterberechnet !== 0 && (
            <>
              <div style={{ fontSize: '11px', color: '#9CA3AF' }}>nicht weiterber.</div>
              <div style={{ fontSize: '13px', color: '#6B7280', marginTop: '1px' }}>
                {formatCurrency(nichtWeiterberechnet)}
              </div>
            </>
          )}
        </div>

        {/* Summe Weiterberechnung */}
        <div style={{
          fontSize: '14px', fontWeight: '600', color: '#111827',
          minWidth: '100px', textAlign: 'right', flexShrink: 0
        }}>
          {match.status === 'ok' ? formatCurrency(match.totalWeiterberechnung) : ''}
        </div>
      </div>

      {/* Warnung */}
      {match.warning && (
        <div style={{
          padding: '8px 16px 12px 72px', fontSize: '12px', color: '#B45309'
        }}>
          {match.warning}
        </div>
      )}

      {/* Summen-Abweichung gegen DATEV-Kopfsumme */}
      {match.summenAbweichung !== null && (
        <div style={{
          padding: '0 16px 12px 72px', fontSize: '12px', color: '#DC2626'
        }}>
          Summenabweichung: DATEV weist {formatCurrency(match.datevSummeLautKopf)} aus,
          {' '}gelesen wurden {formatCurrency(match.parsedSumme)}
          {' '}(Differenz {formatCurrency(match.summenAbweichung)}). Nicht alle Posten wurden erkannt.
        </div>
      )}

      {/* Ausgeklappte Positions-Details */}
      {expanded && canExpand && (
        <div style={{ padding: '0 16px 16px 72px', backgroundColor: '#F9FAFB' }}>
          {match.aggregatedPositions.length > 0 && (
            <PositionBlock
              title="Wird weiterberechnet"
              titleColor="#059669"
              summe={match.totalWeiterberechnung}
            >
              {match.aggregatedPositions.map(p => (
                <React.Fragment key={`ok-${p.artikelnr}`}>
                  <div style={detailCellStyle}>{p.artikelnr}</div>
                  <div style={detailCellStyle}>{p.bezeichnung}</div>
                  <div style={{ ...detailCellStyle, textAlign: 'right' }}>{p.menge} {p.einheit}</div>
                  <div style={{ ...detailCellStyle, textAlign: 'right' }}>{formatCurrency(p.einzelpreis)}</div>
                  <div style={{ ...detailCellStyle, textAlign: 'right', color: '#111827', fontWeight: '500' }}>
                    {formatCurrency(p.gesamtpreis)}
                  </div>
                </React.Fragment>
              ))}
            </PositionBlock>
          )}

          {match.skippedBillablePositions.length > 0 && (
            <PositionBlock
              title="Weiterberechenbar, aber übersprungen"
              titleColor="#B45309"
              summe={match.totalSkippedBillable}
            >
              {match.skippedBillablePositions.map(p => (
                <React.Fragment key={`skip-${p.artikelnr}`}>
                  <div style={detailCellStyle}>{p.artikelnr}</div>
                  <div style={detailCellStyle}>{p.bezeichnung}</div>
                  <div style={{ ...detailCellStyle, textAlign: 'right' }}>{p.menge} {p.einheit}</div>
                  <div style={{ ...detailCellStyle, textAlign: 'right' }}>{formatCurrency(p.einzelpreis)}</div>
                  <div style={{ ...detailCellStyle, textAlign: 'right', color: '#111827', fontWeight: '500' }}>
                    {formatCurrency(p.gesamtpreis)}
                  </div>
                </React.Fragment>
              ))}
            </PositionBlock>
          )}

          {match.nonBillablePositions.length > 0 && (
            <PositionBlock
              title="Nicht weiterberechenbar"
              titleColor="#6B7280"
              summe={match.totalNonBillable}
              lastColumnLabel="Netto"
              fourthColumnLabel="USt"
            >
              {match.nonBillablePositions.map((p, idx) => (
                <React.Fragment key={`nb-${p.artikelnr || p.bezeichnung}-${p.ustSatz}-${idx}`}>
                  <div style={detailCellStyle}>{p.artikelnr || '–'}</div>
                  <div style={detailCellStyle}>{p.bezeichnung}</div>
                  <div style={{ ...detailCellStyle, textAlign: 'right' }}>{p.menge}</div>
                  <div style={{ ...detailCellStyle, textAlign: 'right' }}>{p.ustSatz} %</div>
                  <div style={{ ...detailCellStyle, textAlign: 'right', color: '#111827', fontWeight: '500' }}>
                    {formatCurrency(p.gesamtpreis)}
                  </div>
                </React.Fragment>
              ))}
            </PositionBlock>
          )}

          <div style={{ fontSize: '12px', color: '#6B7280', paddingTop: '10px' }}>
            Summe laut DATEV: <strong style={{ color: '#374151' }}>{formatCurrency(match.datevSummeLautKopf)}</strong>
            {match.zusammensetzung.length > 0 && (
              <div style={{ marginTop: '4px', paddingLeft: '2px' }}>
                {match.zusammensetzung.map((teil, idx) => (
                  <div key={`teil-${idx}`} style={{ color: '#9CA3AF', fontSize: '11px' }}>
                    davon {teil.bezeichnung}: {formatCurrency(teil.summe)}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Positions-Block innerhalb der ausgeklappten Detailansicht.
 * Fünf Spalten: Art.-Nr. | Bezeichnung | Menge | (Einzelpreis oder USt) | Gesamt
 */
const PositionBlock: React.FC<{
  title: string;
  titleColor: string;
  summe: number;
  fourthColumnLabel?: string;
  lastColumnLabel?: string;
  children: React.ReactNode;
}> = ({ title, titleColor, summe, fourthColumnLabel, lastColumnLabel, children }) => (
  <div style={{ paddingTop: '12px' }}>
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      marginBottom: '6px'
    }}>
      <div style={{ fontSize: '12px', fontWeight: '600', color: titleColor }}>{title}</div>
      <div style={{ fontSize: '12px', fontWeight: '600', color: titleColor }}>{formatCurrency(summe)}</div>
    </div>
    <div style={{
      display: 'grid', gridTemplateColumns: 'auto 1fr auto auto auto',
      gap: '6px 16px', fontSize: '12px'
    }}>
      <div style={detailHeaderStyle}>Art.-Nr.</div>
      <div style={detailHeaderStyle}>Bezeichnung</div>
      <div style={{ ...detailHeaderStyle, textAlign: 'right' }}>Menge</div>
      <div style={{ ...detailHeaderStyle, textAlign: 'right' }}>{fourthColumnLabel || 'Einzelpreis'}</div>
      <div style={{ ...detailHeaderStyle, textAlign: 'right' }}>{lastColumnLabel || 'Gesamt'}</div>
      {children}
    </div>
  </div>
);

// ============================================
// ANALYSE-SEKTION (nachgelagert)
// ============================================

const AnalyseSection: React.FC<{ analysis: WeiterberechnungAnalysis }> = ({ analysis }) => {
  const [openArtikel, setOpenArtikel] = useState(false);
  const [openKunden, setOpenKunden] = useState(false);
  const [alleArtikel, setAlleArtikel] = useState(false);
  const [alleKunden, setAlleKunden] = useState(false);

  const s = analysis.summary;
  const artikelRows = alleArtikel ? analysis.artikelRanking : analysis.artikelRanking.slice(0, RANKING_TOP_N);
  const kundenRows = alleKunden ? analysis.kundenRanking : analysis.kundenRanking.slice(0, RANKING_TOP_N);

  return (
    <div style={{ marginTop: '20px' }}>
      <div style={{
        fontSize: '13px', fontWeight: '600', color: '#6B7280',
        textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '10px'
      }}>
        Analyse der DATEV-Rechnung
      </div>

      {/* Kennzahlen */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
        gap: '12px', marginBottom: '16px'
      }}>
        <SummaryBox label="DATEV-Rechnung gesamt" value={formatCurrency(s.totalDatevRechnung)} color="#374151" />
        <SummaryBox label="Davon weiterberechnet" value={formatCurrency(s.totalBetragNetto)} color="#10B981" />
        <SummaryBox label="Nicht weiterberechenbar" value={formatCurrency(s.totalNichtWeiterberechenbar)} color="#6B7280" />
        {s.totalUebersprungenBerechenbar !== 0 && (
          <SummaryBox
            label="Berechenbar, übersprungen"
            value={formatCurrency(s.totalUebersprungenBerechenbar)}
            color="#F59E0B"
          />
        )}
        {s.bloeckeMitAbweichung > 0 && (
          <SummaryBox
            label="Blöcke mit Summenabweichung"
            value={s.bloeckeMitAbweichung.toString()}
            color="#EF4444"
          />
        )}
      </div>

      {/* Teuerste Artikel */}
      <CollapsibleCard
        title={`Teuerste Artikel (${analysis.artikelRanking.length})`}
        open={openArtikel}
        onToggle={() => setOpenArtikel(v => !v)}
      >
        <div style={{
          display: 'grid', gridTemplateColumns: 'auto 1fr auto auto auto auto',
          gap: '6px 16px', fontSize: '12px', padding: '12px 16px'
        }}>
          <div style={detailHeaderStyle}>Art.-Nr.</div>
          <div style={detailHeaderStyle}>Bezeichnung</div>
          <div style={{ ...detailHeaderStyle, textAlign: 'right' }}>Blöcke</div>
          <div style={{ ...detailHeaderStyle, textAlign: 'right' }}>Menge</div>
          <div style={{ ...detailHeaderStyle, textAlign: 'right' }}>Netto</div>
          <div style={detailHeaderStyle}>Weiterberechenbar</div>
          {artikelRows.map((a, idx) => (
            <React.Fragment key={`art-${a.artikelnr || a.bezeichnung}-${idx}`}>
              <div style={detailCellStyle}>{a.artikelnr || '–'}</div>
              <div style={detailCellStyle}>{a.bezeichnung}</div>
              <div style={{ ...detailCellStyle, textAlign: 'right' }}>{a.vorkommen}</div>
              <div style={{ ...detailCellStyle, textAlign: 'right' }}>{a.menge}</div>
              <div style={{ ...detailCellStyle, textAlign: 'right', color: '#111827', fontWeight: '500' }}>
                {formatCurrency(a.gesamtpreis)}
              </div>
              <div style={{ color: a.aufWhitelist ? '#059669' : '#9CA3AF' }}>
                {a.aufWhitelist ? 'ja' : 'nein'}
              </div>
            </React.Fragment>
          ))}
        </div>
        {analysis.artikelRanking.length > RANKING_TOP_N && (
          <ShowAllButton
            alle={alleArtikel}
            gesamt={analysis.artikelRanking.length}
            onClick={() => setAlleArtikel(v => !v)}
          />
        )}
      </CollapsibleCard>

      {/* Kunden nach Gesamtkosten */}
      <CollapsibleCard
        title={`Kunden nach Gesamtkosten (${analysis.kundenRanking.length})`}
        open={openKunden}
        onToggle={() => setOpenKunden(v => !v)}
      >
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr auto auto auto auto',
          gap: '6px 16px', fontSize: '12px', padding: '12px 16px'
        }}>
          <div style={detailHeaderStyle}>Kunde / Block</div>
          <div style={{ ...detailHeaderStyle, textAlign: 'right' }}>Summe laut DATEV</div>
          <div style={{ ...detailHeaderStyle, textAlign: 'right' }}>Weiterberechnet</div>
          <div style={{ ...detailHeaderStyle, textAlign: 'right' }}>Übersprungen</div>
          <div style={{ ...detailHeaderStyle, textAlign: 'right' }}>Nicht berechenbar</div>
          {kundenRows.map(k => (
            <React.Fragment key={`kd-${k.datevMandantennummer}`}>
              <div>
                <div style={{ color: '#374151' }}>{k.customerCompany || k.datevName}</div>
                <div style={{ color: '#9CA3AF', fontSize: '11px', marginTop: '1px' }}>
                  {k.istBeraterBlock ? 'Ohne Mandantenbezug' : `Mandant ${k.datevMandantennummer}`}
                  {k.summenAbweichung !== null && (
                    <span style={{ color: '#DC2626' }}>
                      {' '}• Summenabweichung {formatCurrency(k.summenAbweichung)}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ ...detailCellStyle, textAlign: 'right', color: '#111827', fontWeight: '500' }}>
                {formatCurrency(k.datevSumme)}
              </div>
              <div style={{ textAlign: 'right', color: k.weiterberechnet !== 0 ? '#059669' : '#9CA3AF' }}>
                {k.weiterberechnet !== 0 ? formatCurrency(k.weiterberechnet) : '–'}
              </div>
              <div style={{ textAlign: 'right', color: k.uebersprungenBerechenbar !== 0 ? '#B45309' : '#9CA3AF' }}>
                {k.uebersprungenBerechenbar !== 0 ? formatCurrency(k.uebersprungenBerechenbar) : '–'}
              </div>
              <div style={{ ...detailCellStyle, textAlign: 'right' }}>
                {formatCurrency(k.nichtWeiterberechenbar)}
              </div>
            </React.Fragment>
          ))}
        </div>
        {analysis.kundenRanking.length > RANKING_TOP_N && (
          <ShowAllButton
            alle={alleKunden}
            gesamt={analysis.kundenRanking.length}
            onClick={() => setAlleKunden(v => !v)}
          />
        )}
      </CollapsibleCard>
    </div>
  );
};

const CollapsibleCard: React.FC<{
  title: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ title, open, onToggle, children }) => (
  <div style={{
    backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB',
    borderRadius: '10px', overflow: 'hidden', marginBottom: '12px'
  }}>
    <div
      onClick={onToggle}
      style={{
        padding: '12px 16px', backgroundColor: '#F9FAFB',
        display: 'flex', alignItems: 'center', gap: '8px',
        cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#111827'
      }}
    >
      {open ? <ChevronDown size={16} color="#6B7280" /> : <ChevronRight size={16} color="#6B7280" />}
      {title}
    </div>
    {open && <div style={{ borderTop: '1px solid #E5E7EB' }}>{children}</div>}
  </div>
);

const ShowAllButton: React.FC<{ alle: boolean; gesamt: number; onClick: () => void }> = ({
  alle, gesamt, onClick
}) => (
  <div style={{ padding: '0 16px 14px' }}>
    <button
      onClick={onClick}
      style={{
        padding: '6px 12px', backgroundColor: '#F3F4F6', color: '#374151',
        border: '1px solid #E5E7EB', borderRadius: '6px',
        fontSize: '12px', fontWeight: '500', cursor: 'pointer'
      }}
    >
      {alle ? `Nur die ersten ${RANKING_TOP_N} anzeigen` : `Alle ${gesamt} anzeigen`}
    </button>
  </div>
);

// ============================================
// STATUS/ACTION LABELS
// ============================================

function getStatusInfo(status: string): { label: string; bg: string; color: string; border: string } {
  switch (status) {
    case 'ok':
      return { label: 'OK', bg: '#ECFDF5', color: '#059669', border: '#A7F3D0' };
    case 'no_whitelist_items':
      return { label: 'Keine Whitelist-Posten', bg: '#F3F4F6', color: '#6B7280', border: '#D1D5DB' };
    case 'sammelkunde_skipped':
      return { label: 'Sammelkunde', bg: '#FEF3C7', color: '#92400E', border: '#FDE68A' };
    case 'customer_not_found':
      return { label: 'Kunde nicht gefunden', bg: '#FEE2E2', color: '#991B1B', border: '#FECACA' };
    case 'customer_deleted':
      return { label: 'Kunde gelöscht', bg: '#FEE2E2', color: '#991B1B', border: '#FECACA' };
    case 'berater_block':
      return { label: 'Beraterbezogen', bg: '#EEF2FF', color: '#4338CA', border: '#C7D2FE' };
    default:
      return { label: status, bg: '#F3F4F6', color: '#6B7280', border: '#D1D5DB' };
  }
}

function getActionLabel(action: string): string {
  switch (action) {
    case 'append_draft': return 'Entwurf ergänzen';
    case 'append_draft_next_month': return 'Entwurf ergänzen (Folgemonat)';
    case 'create_draft': return 'Neuer Entwurf';
    case 'create_draft_next_month': return 'Neuer Entwurf (Folgemonat)';
    case 'skip': return '—';
    default: return action;
  }
}
